import React, {FC, forwardRef, useCallback, useEffect, useRef, useState} from 'react';

import {cn} from '@bem-react/classname';
import {Text, UserAvatar} from '@gravity-ui/uikit';
import {Suggest} from '@yandex-data-ui/common';
import {DataSource} from 'common/constants/survey';
import {SuggestItem} from 'common/models/survey';
import {flatten, isEmpty} from 'lodash';
import QuestionLayout, {QuestionLayoutType} from 'ui/components/QuestionLayout';

import SelectedBadge from '../SelectedBadge';

import {SuggestQuestionProps} from './SuggestQuestion.types';

import './SuggestQuestion.scss';

const cnSuggestQuestion = cn('SuggestQuestion');

const iframe = window.YC?.iframe;

const SuggestQuestion: FC<SuggestQuestionProps> = forwardRef<HTMLDivElement, SuggestQuestionProps>(
    function SuggestQuestion({multiple, onChange, value, getItem, parents, error, ...props}, ref) {
        const [text, setText] = useState('');
        const [selected, setSelected] = useState<SuggestItem[]>([]);
        const lastParents = useRef<string[]>(parents ?? []);

        const onSelect = useCallback(
            (item: SuggestItem) => {
                let newValue = [];
                if (multiple) {
                    setText('');
                    const newSelected = selected;
                    if (!selected.some((currItem) => currItem.id === item.id)) {
                        newSelected.push(item);
                    }
                    setSelected(newSelected);
                    newValue = newSelected.map((item) => item.id);
                } else {
                    setText(item.text);
                    newValue = [item.id];
                }

                if (onChange) {
                    onChange(newValue);
                }
            },
            [multiple, onChange, selected],
        );

        const onDelete = useCallback(
            (id: string) => () => {
                const newSelected = selected.filter((item) => item.id !== id);
                setSelected(newSelected);
                if (onChange) {
                    onChange(newSelected.map((item) => item.id));
                }
            },
            [onChange, selected],
        );

        const renderItem = useCallback((item: SuggestItem) => {
            if (item.layer === DataSource.StaffLogin) {
                return (
                    <div className={cnSuggestQuestion('Item')}>
                        <UserAvatar
                            className={cnSuggestQuestion('ItemAvatar')}
                            size="m"
                            imgUrl={item.avatar}
                        />
                        <div className={cnSuggestQuestion('ItemInfo')}>
                            <Text variant="body-1">{item.text}</Text>
                            <Text variant="caption-2" ellipsis color="secondary">
                                {item.department}
                            </Text>
                        </div>
                    </div>
                );
            }
            return <div className={cnSuggestQuestion('Item')}>{item.text}</div>;
        }, []);

        const onUpdateText = useCallback(
            (newText: string) => {
                setText(newText);
                if (!newText && !multiple && onChange) {
                    onChange([]);
                }
            },
            [multiple, onChange],
        );

        useEffect(() => {
            if (!getItem || selected.length || text || isEmpty(value)) {
                return;
            }
            if (multiple) {
                Promise.all((value as string[]).map(getItem)).then((result) => {
                    const filteredResult = result.filter<SuggestItem[]>(
                        (item): item is SuggestItem[] => Boolean(item),
                    );
                    if (filteredResult?.length) {
                        setSelected(flatten(filteredResult));
                    }
                });
            } else {
                getItem((value as string[])[0]).then((result) => {
                    if (result) {
                        setText(result[0]?.text ?? '');
                    }
                });
            }
        }, [getItem, multiple, selected.length, text, value]);

        useEffect(() => {
            const currentParents = parents || [];
            const notChanged =
                currentParents.length === lastParents.current.length &&
                currentParents.every((item) => lastParents.current.includes(item));
            if (notChanged) {
                return;
            }
            setText('');
            if (onChange) onChange([]);
            lastParents.current = currentParents;
        }, [onChange, parents]);

        return (
            <QuestionLayout
                {...props}
                type={QuestionLayoutType.Column}
                className={cnSuggestQuestion('', [props.className])}
                error={typeof error === 'string' ? error : undefined}
                control={
                    <div className={cnSuggestQuestion('Input')} ref={ref}>
                        <Suggest
                            key={parents?.join(',')}
                            renderItem={renderItem}
                            getItemsOnMount
                            showItemsOnNoText
                            popupPlacement={['bottom-start', 'top-start']}
                            syncPopupOnResize
                            size={'l'}
                            debounce={500}
                            hasClear
                            {...props}
                            error={Boolean(error)}
                            text={text}
                            onUpdate={onUpdateText}
                            onItemClick={onSelect}
                            className={cnSuggestQuestion('Suggest')}
                            inputClassName={cnSuggestQuestion('InputControl')}
                            popupClassName={cnSuggestQuestion('Popup', {iframe})}
                        />
                        {multiple && selected.length > 0 && (
                            <div className={cnSuggestQuestion('Selected')}>
                                {selected.map((item) => (
                                    <SelectedBadge
                                        key={item.id}
                                        name={item.text}
                                        onDelete={onDelete(item.id)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                }
            />
        );
    },
);
export default SuggestQuestion;
